import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import CustomToast from "src/components/CustomToast/CustomToast";
import cmsClient from "src/services/cmsClient";

const togglePublished = async ({
	id,
	published
}: {
	id: number;
	published: boolean;
}) => {
	const response = await cmsClient.patch<IQuickLink>(
		`/homepage/admin/quicklink/${id}/`,
		{ published }
	);

	return response.data;
};

const useToggleQuickLinkPublished = () => {
	const queryClient = useQueryClient();

	const onMutate = async (variables: { id: number; published: boolean }) => {
		await queryClient.cancelQueries(["quick-links"]);

		const previousActive = queryClient.getQueryData<IQuickLink[]>([
			"quick-links",
			true
		]);
		const previousDraft = queryClient.getQueryData<IQuickLink[]>([
			"quick-links",
			false
		]);

		const sourceKey = variables.published
			? ["quick-links", false]
			: ["quick-links", true];
		const targetKey = variables.published
			? ["quick-links", true]
			: ["quick-links", false];

		const sourceData = queryClient.getQueryData<IQuickLink[]>(sourceKey) ?? [];
		const targetData = queryClient.getQueryData<IQuickLink[]>(targetKey) ?? [];

		const item = sourceData.find((link) => link.id === variables.id);

		if (item) {
			queryClient.setQueryData<IQuickLink[]>(
				sourceKey,
				sourceData.filter((link) => link.id !== variables.id)
			);
			queryClient.setQueryData<IQuickLink[]>(targetKey, [
				...targetData,
				{ ...item, published: variables.published }
			]);
		}

		return { previousActive, previousDraft };
	};

	const onSuccess = (_data: IQuickLink, variables: { published: boolean }) => {
		const message = variables.published
			? "Quick link is now live"
			: "Quick link moved to drafts";

		toast.success(<CustomToast type="Success" message={message} />, {
			hideProgressBar: true
		});

		queryClient.invalidateQueries(["quick-links"]);
	};

	const onError = (
		_error: unknown,
		_variables: { id: number; published: boolean },
		context?: { previousActive?: IQuickLink[]; previousDraft?: IQuickLink[] }
	) => {
		if (context?.previousActive) {
			queryClient.setQueryData(["quick-links", true], context.previousActive);
		}
		if (context?.previousDraft) {
			queryClient.setQueryData(["quick-links", false], context.previousDraft);
		}

		toast.error(
			<CustomToast type="Error" message="Something went wrong, try again" />,
			{ hideProgressBar: true }
		);
	};

	const mutation = useMutation(togglePublished, {
		onMutate,
		onError,
		onSuccess
	});

	return mutation;
};

export default useToggleQuickLinkPublished;
